// Shared collapsible criteria bar for visuals & dashboards.
//
// Extracted (behaviour-preserving) from the visual preview modal's criteria
// pattern so every surface that runs a visual/dashboard gets the same UX: a
// thin toolbar row with a collapse toggle + a one-line summary of the applied
// criteria when collapsed, and a full-width, scrollable criteria FORM when
// expanded. The host supplies its own action controls (Run / Apply / SQL / row
// count) through the `actions` slot, and owns the collapsed state so it can
// auto-collapse after a successful run.
//
// Internals are inline-styled (no app `btn` CSS classes) so the standalone
// runtime — which doesn't ship those classes — renders identically.

import { useMemo } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { ChevronDown, ChevronRight, SlidersHorizontal } from 'lucide-react'
import VisualCriteriaForm from './VisualCriteriaForm'
import { issuesFor } from './criteriaFormat'
import { tw } from '../testHooks'
import type { CriteriaIssue, VisualCriteriaPromptVar, VisualCriteriaRuntime } from './VisualCriteriaForm'

export interface CriteriaBarProps {
  project: string
  promptVars: VisualCriteriaPromptVar[]
  /** Current input values keyed by prompt name. */
  values: Record<string, string>
  onChange: (name: string, val: string) => void
  /** Fired when the form is submitted (Enter) — usually re-runs the query. */
  onSubmit: () => void
  runtime: VisualCriteriaRuntime
  collapsed: boolean
  onToggleCollapsed: () => void
  /** Host action controls rendered on the right of the toolbar row. */
  actions?: ReactNode
  /** Findings of the last run's checks (`criteriaErrors` + `criteriaNotices`,
   *  or the host's own required check). Each shows under the criterion it
   *  names; a warning — the run went ahead, so the bar may be folded — and
   *  anything naming no visible criterion also show under the toolbar. */
  issues?: CriteriaIssue[]
  /** 'band' (default) — the thin toolbar strip every visual surface uses.
   *  'card' — a dashboard SCREEN's filter card (DashboardFilterCard): sits on
   *  the card's own surface, a labelled "Filters" toggle, and the applied
   *  criteria as chips that open the form. */
  look?: 'band' | 'card'
}

// look="card": no surfaces of its own (the card supplies one), a divider only
// between the toolbar and what opens under it.
const cardToolbarStyle: CSSProperties = {
  display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
  padding: '8px 12px', flexShrink: 0,
}
const cardToggleStyle: CSSProperties = {
  height: 34, display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0,
  padding: '0 10px', borderRadius: 8, border: 'none', background: 'transparent',
  color: 'var(--color-text)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
}
const cardChipStyle: CSSProperties = {
  height: 30, display: 'inline-flex', alignItems: 'center', gap: 8, maxWidth: '100%',
  padding: '0 12px', borderRadius: 15, border: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
  fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden',
}
const cardFormRegionStyle: CSSProperties = {
  flexShrink: 0, padding: '14px 16px 16px', borderTop: '1px solid var(--color-border)',
}
const cardStripStyle: CSSProperties = {
  display: 'grid', gap: 2, padding: '8px 16px', fontSize: 13,
  borderTop: '1px solid var(--color-border)', flexShrink: 0,
}

const stripStyle: CSSProperties = {
  display: 'grid', gap: 2, padding: '6px 12px', fontSize: 13,
  borderBottom: '1px solid var(--color-border)', flexShrink: 0,
}

const toolbarStyle: CSSProperties = {
  display: 'flex', gap: 10, alignItems: 'center',
  padding: '8px 12px', borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))', flexShrink: 0,
}

const toggleBtnStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
  background: 'none', border: '1px solid var(--color-border)', borderRadius: 6,
  padding: '3px 6px', cursor: 'pointer', color: 'var(--color-text-secondary)',
}

const formRegionStyle: CSSProperties = {
  flexShrink: 0, maxHeight: '40%', overflowY: 'auto',
  padding: '14px 16px', borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2, var(--color-bg-secondary))',
}

export default function CriteriaBar({
  project, promptVars, values, onChange, onSubmit, runtime,
  collapsed, onToggleCollapsed, actions, issues, look = 'band',
}: CriteriaBarProps) {
  const card = look === 'card'
  // A prompt the tenant hid always runs on its default: not a filter the
  // user sees or counts.
  const visibleVars = useMemo(() => promptVars.filter((pv) => !pv.hidden), [promptVars])
  const hasFilters = visibleVars.length > 0
  // "Label: value" pairs shown when collapsed — only the ones with a value.
  const filterChips = useMemo(
    () => visibleVars
      .map((pv) => ({ label: pv.label, value: String(values[pv.name] ?? '').trim() }))
      .filter((c) => c.value !== ''),
    [visibleVars, values],
  )

  // What the fields cannot show: notices (visible even while folded) and any
  // finding that names no visible criterion.
  const stripIssues = useMemo(
    () => (issues || []).filter((i) => (i.severity || 'E') !== 'E'
      || !visibleVars.some((pv) => issuesFor([i], pv.name).length > 0)
      || collapsed),
    [issues, visibleVars, collapsed],
  )
  const labelOf = (param: string) =>
    visibleVars.find((pv) => issuesFor([{ param, message: '' }], pv.name).length)?.label || ''

  return (
    <>
      <div style={card ? cardToolbarStyle : toolbarStyle}>
        {hasFilters && card && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            {...tw('action', 'toggle_criteria')}
            title={collapsed ? 'Show filters' : 'Hide filters'}
            aria-expanded={!collapsed}
            style={cardToggleStyle}
          >
            <SlidersHorizontal size={16} />
            Filters
            <ChevronDown size={16} style={{
              color: 'var(--color-text-secondary)', transition: 'transform 150ms ease',
              transform: collapsed ? 'none' : 'rotate(180deg)',
            }} />
          </button>
        )}
        {hasFilters && !card && (
          <button
            type="button"
            onClick={onToggleCollapsed}
            {...tw('action', 'toggle_criteria')}
            title={collapsed ? 'Show criteria' : 'Hide criteria'}
            aria-label={collapsed ? 'Show criteria' : 'Hide criteria'}
            style={toggleBtnStyle}
          >
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
            <SlidersHorizontal size={14} />
          </button>
        )}

        {/* One-line applied-criteria summary while collapsed. */}
        {/* look="card": the applied criteria as chips; a chip opens the form. */}
        {card && (
          <div style={{ flex: '1 1 280px', minWidth: 0, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
            {hasFilters && collapsed && filterChips.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={onToggleCollapsed}
                title={`${c.label}: ${c.value}`}
                style={cardChipStyle}
              >
                <span style={{ color: 'var(--color-text-secondary)' }}>{c.label}</span>
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.value}</span>
              </button>
            ))}
            {hasFilters && collapsed && filterChips.length === 0 && (
              <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                {visibleVars.length} {visibleVars.length === 1 ? 'filter' : 'filters'} · defaults
              </span>
            )}
          </div>
        )}
        {!card && (
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
          {hasFilters && collapsed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)', flexShrink: 0 }}>
                {visibleVars.length} criteri{visibleVars.length === 1 ? 'on' : 'a'}
              </span>
              {filterChips.length > 0 && (
                <span style={{
                  fontSize: 14, color: 'var(--color-text-secondary)', minWidth: 0,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {filterChips.map((c) => `${c.label}: ${c.value}`).join('  •  ')}
                </span>
              )}
            </div>
          )}
        </div>
        )}

        {actions && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {actions}
          </div>
        )}
      </div>

      {stripIssues.length > 0 && (
        <div {...tw('msg', 'criteria-notes')} role="status" style={card ? cardStripStyle : stripStyle}>
          {stripIssues.map((i, n) => {
            const label = labelOf(i.param)
            return (
              <span key={n} style={{
                color: (i.severity || 'E') === 'E' ? 'var(--color-error)' : 'var(--color-text-secondary)',
              }}>
                {label ? `${label}: ` : ''}{i.message}
              </span>
            )
          })}
        </div>
      )}

      {hasFilters && !collapsed && (
        <div style={card ? cardFormRegionStyle : formRegionStyle}>
          <VisualCriteriaForm
            project={project}
            promptVars={promptVars}
            values={values}
            onChange={onChange}
            onSubmit={onSubmit}
            runtime={runtime}
            issues={issues}
          />
        </div>
      )}
    </>
  )
}
